import React, { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { completeLogin, verifyCredentials } from '../../api/auth';
import { ApiError, UnauthorizedError } from '../../api/client';
import { AllowedTenant } from '../../types/auth';
import { CredentialsStep } from './CredentialsStep';
import { TenantSelectStep } from './TenantSelectStep';
import { t } from '../../i18n/t';
import './LoginPage.css';

type Step = 'credentials' | 'tenantSelect';

interface LocationState {
  from?: { pathname?: string };
}

export function LoginPage() {
  const { status, login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const from =
    (location.state as LocationState | null)?.from?.pathname ?? '/settings/banks';

  const [step, setStep] = useState<Step>('credentials');
  const [intermediateToken, setIntermediateToken] = useState<string | null>(null);
  const [allowedTenants, setAllowedTenants] = useState<AllowedTenant[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (status === 'authenticated') {
    return <Navigate to={from} replace />;
  }
  if (status === 'bootstrapping') {
    return null;
  }

  async function handleCredentialsSubmit(email: string, password: string) {
    setError(null);
    setIsSubmitting(true);
    try {
      const res = await verifyCredentials({ email, password });
      if (!res.isMultiTenant) {
        const tenant = res.allowedTenants[0];
        if (!tenant) {
          setError(t('auth.serverDataError'));
          return;
        }
        const completeRes = await completeLogin({
          intermediateToken: res.intermediateToken,
          tenantId: tenant.tenantId,
        });
        login(completeRes.user);
        navigate(from, { replace: true });
      } else {
        setIntermediateToken(res.intermediateToken);
        setAllowedTenants(res.allowedTenants);
        setStep('tenantSelect');
      }
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        setError(t('auth.invalidCredentials'));
      } else if (err instanceof ApiError && err.status === 429) {
        setError(t('auth.tooManyAttempts'));
      } else {
        setError(err instanceof Error ? err.message : t('auth.unexpectedError'));
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleTenantSubmit(tenantId: number) {
    if (!intermediateToken) return;
    setError(null);
    setIsSubmitting(true);
    try {
      const res = await completeLogin({ intermediateToken, tenantId });
      login(res.user);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        setError(t('auth.tokenExpired'));
        setStep('credentials');
        setIntermediateToken(null);
        setAllowedTenants([]);
      } else if (err instanceof ApiError && err.status === 429) {
        setError(t('auth.tooManyAttempts'));
      } else {
        setError(err instanceof Error ? err.message : t('auth.unexpectedError'));
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleBack() {
    setStep('credentials');
    setIntermediateToken(null);
    setAllowedTenants([]);
    setError(null);
  }

  return (
    <div className="login-page">
      <div className="login-page__card">
        <h1>{t('auth.loginTitle')}</h1>
        {step === 'credentials' && (
          <CredentialsStep
            onSubmit={handleCredentialsSubmit}
            isSubmitting={isSubmitting}
            error={error}
          />
        )}
        {step === 'tenantSelect' && (
          <TenantSelectStep
            allowedTenants={allowedTenants}
            onSubmit={handleTenantSubmit}
            onBack={handleBack}
            isSubmitting={isSubmitting}
            error={error}
          />
        )}
      </div>
    </div>
  );
}
